import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';
import PerformancesFilters from './components/PerformancesFilters';
import PerformancesKPICards from './components/PerformancesKPICards';
import ServerComparison from './components/ServerComparison';
import ManagerComparison from './components/ManagerComparison';
import PerformancesTable from './components/PerformancesTable';
import Top10Chart from './components/Top10Chart';
import MonthlyEvolutionChart from './components/MonthlyEvolutionChart';
import TierDistributionChart from './components/TierDistributionChart';

const PerformancesManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Filter state
  const [periods, setPeriods] = useState([]);
  const [periodsLoading, setPeriodsLoading] = useState(true);
  const [selectedPeriods, setSelectedPeriods] = useState([]);
  const [selectedServer, setSelectedServer] = useState('All');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [tableSearch, setTableSearch] = useState('');

  // Data state
  const [tableData, setTableData] = useState([]);
  const [levelMap, setLevelMap] = useState({});
  const [managersData, setManagersData] = useState([]);
  const [serversData, setServersData] = useState([]);
  const [evolutionData, setEvolutionData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  // Get current user
  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (session?.user?.email) setUserEmail(session?.user?.email);
    });
  }, []);

  // FIX B1: Read server from URL param on mount
  useEffect(() => {
    const serverParam = searchParams?.get('server');
    if (serverParam && serverParam !== selectedServer) {
      setSelectedServer(serverParam);
    }
  }, []);

  // Fetch active servers dynamically
  useEffect(() => {
    const fetchServers = async () => {
      const { data } = await supabase?.from('servers')?.select('id, name, color')?.eq('is_active', true);
      setServersData(data || []);
    };
    fetchServers();
  }, []);

  // Fetch distinct periods from creator_monthly_history
  useEffect(() => {
    const fetchPeriods = async () => {
      setPeriodsLoading(true);
      try {
        const { data: periodsData } = await supabase?.rpc('get_distinct_periods');
        const distinctPeriods = periodsData?.map(r => r?.period) || [];
        setPeriods(distinctPeriods);
        if (distinctPeriods?.length > 0) setSelectedPeriods([distinctPeriods?.[0]]);
      } catch (err) {
        console.error('Periods fetch error:', err);
      } finally {
        setPeriodsLoading(false);
      }
    };
    fetchPeriods();
  }, []);

  // Fetch managers by role
  useEffect(() => {
    const fetchManagers = async () => {
      const { data } = await supabase?.from('users')?.select('id, name')?.in('role', ['manager', 'region_manager'])?.order('name');
      setManagersData(data || []);
    };
    fetchManagers();
  }, []);

  // Fetch creator levels from creators table
  useEffect(() => {
    const fetchLevelMap = async () => {
      const { data: creatorsData } = await supabase?.from('creators')?.select('tiktok_id, level');
      const map = {};
      creatorsData?.forEach(c => { if (c?.tiktok_id) map[c.tiktok_id] = c?.level; });
      setLevelMap(map);
    };
    fetchLevelMap();
  }, []);

  // Fetch evolution data for monthly chart (all periods, grouped by period+server)
  useEffect(() => {
    const fetchEvolution = async () => {
      const { data } = await supabase?.from('creator_monthly_history')?.select('period, server, diamonds')?.order('period', { ascending: true });
      setEvolutionData(data || []);
    };
    fetchEvolution();
  }, []);

  // FIX A1-A: Fetch main data when filters change — server filter applied server-side
  const fetchData = useCallback(async () => {
    if (!selectedPeriods?.length) return;
    setDataLoading(true);
    try {
      let query = supabase?.from('creator_monthly_history')?.select(`
          tiktok_id,
          username,
          display_name,
          server,
          period,
          manager_id,
          group_name,
          diamonds,
          live_hours,
          live_days,
          followers,
          tier_p,
          activeness_level,
          activeness_bonus,
          rankup_bonus,
          incremental_bonus,
          total_bonus,
          charge,
          cashback,
          net_agency,
          is_violative,
          is_estimated,
          users!creator_monthly_history_manager_id_fkey(id, name)
        `)?.in('period', selectedPeriods)?.order('diamonds', { ascending: false });

      // FIX A1-A: Apply server filter to main query
      if (selectedServer !== 'All') query = query?.eq('server', selectedServer);
      if (selectedManagerId) query = query?.eq('manager_id', selectedManagerId);

      const { data, error } = await query;
      if (error) throw error;
      setTableData(data || []);
    } catch (err) {
      console.error('Data fetch error:', err);
      setTableData([]);
    } finally {
      setDataLoading(false);
    }
  }, [selectedPeriods, selectedServer, selectedManagerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Apply client-side level filter using levelMap
  const filteredTableData = useMemo(() => {
    if (!selectedLevel) return tableData;
    return tableData?.filter(r => levelMap?.[r?.tiktok_id] === selectedLevel);
  }, [tableData, selectedLevel, levelMap]);

  // KPI calculations from filtered data
  const kpiData = useMemo(() => {
    if (!filteredTableData?.length) {
      return { totalDiamonds: 0, totalBonus: 0, totalCharge: 0, activeCreators: 0, netAgence: 0, avgDiamonds: 0 };
    }
    const totalDiamonds = filteredTableData?.reduce((sum, r) => sum + (parseInt(r?.diamonds) || 0), 0);
    const totalBonus = filteredTableData?.reduce((sum, r) => sum + (parseFloat(r?.total_bonus) || 0), 0);
    const totalCharge = filteredTableData?.reduce((sum, r) => sum + (parseFloat(r?.charge) || 0), 0);
    const activeCreators = filteredTableData?.filter(r => (parseInt(r?.diamonds) || 0) > 0)?.length;
    const netAgence = filteredTableData?.reduce((sum, r) => sum + (parseFloat(r?.net_agency) || 0), 0);
    const avgDiamonds = activeCreators > 0 ? totalDiamonds / activeCreators : 0;
    return { totalDiamonds, totalBonus, totalCharge, activeCreators, netAgence, avgDiamonds };
  }, [filteredTableData]);

  const handleReset = () => {
    setSelectedServer('All');
    setSelectedLevel('');
    setSelectedManagerId('');
    setTableSearch('');
    if (periods?.length > 0) setSelectedPeriods([periods?.[0]]);
  };

  const handlePeriodToggle = (period) => {
    setSelectedPeriods(prev => {
      if (prev?.includes(period)) {
        if (prev?.length === 1) return prev;
        return prev?.filter(p => p !== period);
      }
      return [...prev, period];
    });
  };

  const SectionHeader = ({ color, label }) => (
    <div className="flex items-center gap-2 mb-4">
      <div className="w-1 h-4 rounded-full" style={{ backgroundColor: color }} />
      <h2
        className="text-xs font-semibold uppercase tracking-widest"
        style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}
      >
        {label}
      </h2>
    </div>
  );

  return (
    <div
      className="flex min-h-screen"
      style={{ backgroundColor: 'var(--color-background)', fontFamily: 'Inter, sans-serif' }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Sora:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      <div className="hidden md:flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(v => !v)}
          userEmail={userEmail}
        />
      </div>
      <main className="flex-1 overflow-y-auto min-w-0 pb-16 md:pb-0">
        <AdminMobileHeader title={t('admin.performance.historyTitle', 'Historique Mensuel')} />
        {/* Header */}
        <div
          className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b"
          style={{ backgroundColor: 'var(--color-background)', borderColor: '#1f1f1f' }}
        >
          <div>
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
              {t('admin.performance.historyTitle', 'Historique Mensuel')}
            </h1>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
              {t('admin.performance.historySubtitle', 'Historique mensuel par créateur — creator_monthly_history')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {selectedPeriods?.map(p => (
              <div
                key={p}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border"
                style={{
                  backgroundColor: 'rgba(229,32,42,0.08)',
                  borderColor: 'rgba(229,32,42,0.2)',
                  color: '#E5202A',
                  fontFamily: 'Sora, sans-serif',
                }}
              >
                {p}
              </div>
            ))}
            {selectedPeriods?.length > 1 && (
              <div
                className="px-2 py-1 rounded text-xs font-semibold"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}
              >
                {selectedPeriods?.length} {t('admin.performance.periodsLabel', 'périodes')}
              </div>
            )}
          </div>
        </div>

        {/* Estimated data warning */}
        {tableData?.some(r => r?.is_estimated) && (
          <div className="mx-6 mt-4 px-4 py-3 rounded-lg border" style={{ backgroundColor: 'rgba(249,115,22,0.06)', borderColor: 'rgba(249,115,22,0.2)' }}>
            <p className="text-xs font-medium" style={{ color: '#f97316' }}>
              ⚠️ {t('admin.performance.estimatedWarning', 'Données à titre indicatif — Les données marquées "Estimé" sont calculées à partir des données journalières et seront ajustées après injection du fichier TYPE 2.')}
            </p>
          </div>
        )}

        {/* Filters Bar */}
        <PerformancesFilters
          periods={periods}
          periodsLoading={periodsLoading}
          selectedPeriods={selectedPeriods}
          onPeriodToggle={handlePeriodToggle}
          selectedServer={selectedServer}
          onServerChange={setSelectedServer}
          onReset={handleReset}
          selectedLevel={selectedLevel}
          onLevelChange={setSelectedLevel}
          selectedManagerId={selectedManagerId}
          onManagerChange={setSelectedManagerId}
          managersData={managersData}
          serversData={serversData}
        />

        {/* Content */}
        <div className="p-6 space-y-8">

          {/* KPI Cards */}
          <section>
            <SectionHeader
              color="#f59e0b"
              label={selectedPeriods?.length > 1 ? t('admin.performance.kpiCombined', 'KPI — {{count}} périodes combinées', { count: selectedPeriods?.length }) : t('admin.performance.kpiSummary', 'KPI — Résumé de la période')}
            />
            <PerformancesKPICards data={kpiData} loading={dataLoading} />
          </section>

          {/* Server Comparison — FIX A1-F: only show when Tous */}
          {selectedServer === 'All' && (
            <section>
              <SectionHeader color="#f97316" label={t('admin.performance.serverComparison', 'Comparaison par Serveur')} />
              <ServerComparison allData={filteredTableData} loading={dataLoading} serversData={serversData} />
            </section>
          )}

          {/* Manager Comparison — FIX A1-D: filteredTableData already server-filtered */}
          <section>
            <SectionHeader color="#f59e0b" label={t('admin.performance.managerComparisonLabel', 'Comparaison par Manager')} />
            <ManagerComparison tableData={filteredTableData} loading={dataLoading} serversData={serversData} />
          </section>

          {/* Monthly Evolution Chart */}
          <section>
            <SectionHeader color="#10B981" label={t('admin.performance.monthlyEvolution', 'Évolution Mensuelle — Diamonds par Serveur')} />
            <MonthlyEvolutionChart data={evolutionData} loading={dataLoading} serversData={serversData} selectedServer={selectedServer} />
          </section>

          {/* Tier Distribution */}
          {selectedPeriods?.length === 1 && (
            <section>
              <SectionHeader color="#E5202A" label={`${t('admin.performance.tierDistribution', 'Distribution des Tiers')} — ${selectedPeriods?.[0]}`} />
              <TierDistributionChart data={filteredTableData} loading={dataLoading} />
            </section>
          )}

          {/* Main Table */}
          <section>
            <SectionHeader color="#3b82f6" label={t('admin.performance.creatorPerformance', 'Performance Créateurs')} />
            <PerformancesTable
              data={filteredTableData}
              loading={dataLoading}
              levelMap={levelMap}
              managersData={managersData}
              serversData={serversData}
              tableSearch={tableSearch}
              onTableSearchChange={setTableSearch}
              onRowClick={tiktokId => navigate(`/admin/creators/${tiktokId}`)}
              selectedPeriods={selectedPeriods}
            />
          </section>

          {/* Top 10 Chart */}
          <section>
            <SectionHeader color="#a855f7" label={t('admin.performance.top10', 'Top 10 Créateurs — Diamonds')} />
            <Top10Chart data={filteredTableData} loading={dataLoading} serversData={serversData} />
          </section>
        </div>
      </main>
      <AdminBottomNav />
    </div>
  );
};

export default PerformancesManagement;
